import { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { MapContainer, TileLayer, Marker, Popup, Polyline } from 'react-leaflet';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Navigation, User, Clock, MapPin } from 'lucide-react';
import { toast } from 'sonner';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

// Fix Leaflet default marker icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

const LiveTracking = () => {
  const [locations, setLocations] = useState([]);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [driverHistory, setDriverHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const intervalRef = useRef(null);

  useEffect(() => {
    loadLocations();
    
    // Auto-refresh every 30 seconds
    intervalRef.current = setInterval(loadLocations, 30000);
    
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  const loadLocations = async () => {
    try {
      const res = await axios.get('/gps/active');
      setLocations(res.data);
    } catch (error) {
      console.error('Error loading locations:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadDriverHistory = async (userId) => {
    try {
      const res = await axios.get(`/gps/history/${userId}?hours=8`);
      setDriverHistory(res.data);
    } catch (error) {
      console.error('Error loading history:', error);
      toast.error('Fehler beim Laden der Route');
    }
  };

  const handleDriverClick = (location) => {
    setSelectedDriver(location);
    loadDriverHistory(location.user_id);
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
  };

  const getTimeSince = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const minutes = Math.floor((now - date) / 1000 / 60);
    
    if (minutes < 1) return 'Gerade eben';
    if (minutes === 1) return 'Vor 1 Minute';
    if (minutes < 60) return `Vor ${minutes} Minuten`;
    
    const hours = Math.floor(minutes / 60);
    return `Vor ${hours} Stunde${hours > 1 ? 'n' : ''}`;
  };

  // Calculate center of all markers
  const mapCenter = locations.length > 0
    ? [locations[0].latitude, locations[0].longitude]
    : [52.520008, 13.404954]; // Berlin default

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="live-tracking">
      <div>
        <h1 className="text-4xl md:text-5xl font-black tracking-tight uppercase text-white mb-2">
          Live Tracking
        </h1>
        <p className="text-zinc-400">Echtzeit-Standorte aller aktiven Fahrer</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Map */}
        <div className="lg:col-span-2">
          <Card className="bg-zinc-900 border-zinc-800 overflow-hidden">
            <CardContent className="p-0">
              <div style={{ height: '600px', width: '100%' }}>
                {typeof window !== 'undefined' && (
                  <MapContainer
                    center={mapCenter}
                    zoom={13}
                    style={{ height: '100%', width: '100%' }}
                  >
                    <TileLayer
                      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                      attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                    />
                    
                    {/* Active locations */}
                    {locations.map((loc, index) => (
                      <Marker
                        key={index}
                        position={[loc.latitude, loc.longitude]}
                        eventHandlers={{
                          click: () => handleDriverClick(loc)
                        }}
                      >
                        <Popup>
                          <div className="text-sm">
                            <strong>{loc.user_name}</strong><br />
                            {formatTime(loc.timestamp)}<br />
                            {loc.speed && `${Math.round(loc.speed)} km/h`}
                          </div>
                        </Popup>
                      </Marker>
                    ))}
                    
                    {/* Route history for selected driver */}
                    {driverHistory.length > 1 && (
                      <Polyline
                        positions={driverHistory.map(h => [h.latitude, h.longitude])}
                        color="#dc2626"
                        weight={3}
                        opacity={0.7}
                      />
                    )}
                  </MapContainer>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Driver List */}
        <div>
          <Card className="bg-zinc-900 border-zinc-800">
            <CardHeader>
              <CardTitle className="text-xl font-bold text-white">
                Aktive Fahrer ({locations.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 max-h-[540px] overflow-y-auto">
                {locations.length === 0 ? (
                  <div className="text-center py-8 text-zinc-500">
                    <MapPin className="h-12 w-12 mx-auto mb-2 opacity-50" />
                    <p>Keine aktiven Fahrer</p>
                  </div>
                ) : (
                  locations.map((loc, index) => (
                    <div
                      key={index}
                      onClick={() => handleDriverClick(loc)}
                      className={`p-4 rounded-lg border cursor-pointer transition-all ${
                        selectedDriver?.user_id === loc.user_id
                          ? 'bg-primary/20 border-primary'
                          : 'bg-zinc-800 border-zinc-700 hover:border-zinc-600'
                      }`}
                      data-testid={`driver-${loc.user_id}`}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <div className="h-10 w-10 rounded-full bg-zinc-700 flex items-center justify-center">
                            <User className="h-5 w-5 text-zinc-300" />
                          </div>
                          <div>
                            <div className="font-bold text-white">{loc.user_name}</div>
                            <div className="text-xs text-zinc-400 flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              {getTimeSince(loc.timestamp)}
                            </div>
                          </div>
                        </div>
                        <Badge variant="default" className="bg-green-600">
                          Online
                        </Badge>
                      </div>
                      
                      {loc.speed && (
                        <div className="flex items-center gap-2 text-sm text-zinc-400 mt-2">
                          <Navigation className="h-4 w-4" />
                          <span>{Math.round(loc.speed)} km/h</span>
                        </div>
                      )}
                      
                      {loc.vehicle_id && (
                        <div className="text-xs text-zinc-500 mt-1">
                          Fahrzeug: {loc.vehicle_id}
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Selected Driver Details */}
      {selectedDriver && (
        <Card className="bg-zinc-900 border-zinc-800">
          <CardHeader>
            <CardTitle className="text-xl font-bold text-white">
              Route: {selectedDriver.user_name}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <div className="text-sm text-zinc-400">Letztes Update</div>
                <div className="font-bold text-white">{formatTime(selectedDriver.timestamp)}</div>
              </div>
              <div>
                <div className="text-sm text-zinc-400">Position</div>
                <div className="font-mono text-sm text-white">
                  {selectedDriver.latitude.toFixed(5)}, {selectedDriver.longitude.toFixed(5)}
                </div>
              </div>
              <div>
                <div className="text-sm text-zinc-400">Geschwindigkeit</div>
                <div className="font-bold text-white">
                  {selectedDriver.speed ? `${Math.round(selectedDriver.speed)} km/h` : 'N/A'}
                </div>
              </div>
              <div>
                <div className="text-sm text-zinc-400">Routen-Punkte (8h)</div>
                <div className="font-bold text-white">{driverHistory.length}</div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default LiveTracking;
